/**
 * InsumosEditor — ferragens/acessórios (InsumoAmbiente) de UM alvo: um móvel
 * (`itemId`) ou os lançamentos legados sem móvel (`itemId = null`).
 *
 * Reformulação UX 2026-07-31: o bloco por ambiente virou editor POR MÓVEL.
 * Ação única "Adicionar ferragem" (select de ferragem/fita/acessório + qtde
 * na unidade do produto, vinda do catálogo). Vale nos três níveis (R9).
 *
 * Produto sem preço (pendente/custo zero) ganha Pill warning "sem preço" +
 * link para /estoque/pendentes (gate G8 no envio).
 * Dados vêm prontos do pai (`linhas` já filtradas por alvo); após mutação
 * chama `onMutate` (pai recarrega + dispara "orc2:preco-invalidado").
 */
import React, { useMemo, useState } from "react";
import { FiPlus, FiSave, FiTrash2, FiX } from "react-icons/fi";
import { Link } from "react-router-dom";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import {
  atualizarInsumoAmbiente,
  criarInsumoAmbiente,
  removerInsumoAmbiente,
} from "../../../services/motorPreco";

import { fmtMoeda, fmtNumero } from "../helpers";
import {
  ComposicaoInfoBanner,
  InlineHint,
  Input,
  Pill,
  SecondaryButton,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "../styles";

// Texto obrigatório da spec (§1.6) — não parafrasear.
const AVISO_FITA =
  "A fita de borda está no custo (metragem calculada pelos lados marcados, "
  + "com 5% de folga), mas não é descontada da medida da peça no plano de corte.";

// Categorias de fita — disparam o aviso da spec quando presentes nas linhas.
const CATEGORIAS_FITA = new Set(["BORDA", "FITA"]);

export default function InsumosEditor({
  ambienteId,
  itemId = null,
  linhas = [],
  produtos = [],
  editavel = true,
  permitirCriar = true,
  titulo = "Ferragens e acessórios",
  onMutate,
}) {
  const { request } = useAuth();

  const [edits, setEdits] = useState({});
  // Mini-form de criacao: null | {produto, quantidade}
  const [novo, setNovo] = useState(null);

  // Índice do catálogo por id — detecta "sem preço" e mostra a unidade do
  // produto escolhido, tudo em memória (zero rede por tecla).
  const catalogoPorId = useMemo(() => {
    const mapa = new Map();
    (produtos || []).forEach((p) => mapa.set(String(p.id), p));
    return mapa;
  }, [produtos]);

  const aposMutacao = async () => {
    setEdits({});
    if (onMutate) await onMutate();
  };

  const adicionarLinha = async () => {
    const dados = {
      ambiente: ambienteId,
      produto: novo.produto,
      quantidade: String(novo.quantidade).replace(",", "."),
    };
    if (itemId) dados.item = itemId;
    await criarInsumoAmbiente(request, dados);
    setNovo(null);
    await aposMutacao();
  };

  const salvarLinha = async (linha) => {
    await atualizarInsumoAmbiente(request, linha.id, {
      quantidade: String(edits[linha.id]).replace(",", "."),
    });
    await aposMutacao();
  };

  const removerLinha = async (linha) => {
    await removerInsumoAmbiente(request, linha.id);
    await aposMutacao();
  };

  const produtoNovo = novo ? catalogoPorId.get(String(novo.produto)) : null;
  const novoValido = novo
    && Boolean(novo.produto)
    && Number(String(novo.quantidade).replace(",", ".")) > 0;

  const temFita = linhas.some((linha) => {
    const p = catalogoPorId.get(String(linha.produto));
    return p && CATEGORIAS_FITA.has(p.categoria);
  });

  if (linhas.length === 0 && !(editavel && permitirCriar)) return null;

  return (
    <section>
      {titulo && <SectionTitle>{titulo}</SectionTitle>}

      {linhas.length > 0 && (
        <TableWrapper>
          <Table>
            <colgroup>
              <col />
              <col style={{ width: "24%" }} />
              <col style={{ width: "18%" }} />
              {editavel && <col style={{ width: 84 }} />}
            </colgroup>
            <thead>
              <tr>
                <th>Produto</th>
                <th>Quantidade</th>
                <th>Subtotal</th>
                {editavel && <th aria-label="Ações" />}
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha) => {
                const produtoCat = catalogoPorId.get(String(linha.produto));
                const semPreco = produtoCat
                  ? produtoCat.pendente_cadastro
                    || Number(produtoCat.custo_medio || 0) === 0
                  : Number(linha.custo_calculado || 0) === 0;
                const dirty = edits[linha.id] !== undefined
                  && String(edits[linha.id]) !== String(linha.quantidade);
                return (
                  <tr key={linha.id}>
                    <td title={`${linha.produto_codigo} — ${linha.produto_nome}`}>
                      {linha.produto_nome}
                      {semPreco && (
                        <>
                          {" "}
                          <Pill
                            $tone="warning"
                            title="O estoque ainda não precificou. Dá para orçar, mas não enviar ao cliente (G8)."
                          >
                            sem preço
                          </Pill>{" "}
                          <Link to="/estoque/pendentes">fila de pendentes</Link>
                        </>
                      )}
                    </td>
                    <td>
                      {editavel ? (
                        <>
                          <Input
                            style={{ width: 80, textAlign: "right" }}
                            inputMode="decimal"
                            value={edits[linha.id] ?? String(linha.quantidade)}
                            onChange={(e) =>
                              setEdits((prev) => ({
                                ...prev,
                                [linha.id]: e.target.value,
                              }))
                            }
                            aria-label={`Quantidade de ${linha.produto_nome}`}
                          />{" "}
                          <InlineHint>{linha.produto_unidade}</InlineHint>
                        </>
                      ) : (
                        `${fmtNumero(linha.quantidade, 2)} ${linha.produto_unidade || ""}`
                      )}
                    </td>
                    <td>{fmtMoeda(linha.custo_calculado)}</td>
                    {editavel && (
                      <td>
                        {dirty && (
                          <AsyncButton
                            variant="icon"
                            onClick={() => salvarLinha(linha)}
                            successMessage="Insumo atualizado."
                            title="Salvar quantidade"
                          >
                            <FiSave size={12} />
                          </AsyncButton>
                        )}{" "}
                        <AsyncButton
                          variant="icon"
                          onClick={() => removerLinha(linha)}
                          successMessage="Insumo removido."
                          title="Remover insumo"
                        >
                          <FiTrash2 size={12} />
                        </AsyncButton>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      )}

      {editavel && permitirCriar && !novo && (
        <div style={{ marginTop: 8 }}>
          <SecondaryButton
            type="button"
            onClick={() => setNovo({ produto: "", quantidade: "" })}
          >
            <FiPlus size={12} /> Adicionar ferragem
          </SecondaryButton>
        </div>
      )}

      {editavel && permitirCriar && novo && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
          <Select
            value={novo.produto}
            onChange={(e) =>
              setNovo((p) => ({ ...p, produto: e.target.value }))
            }
            aria-label="Produto (ferragem, fita, acessório)"
          >
            <option value="">Produto (ferragem, fita, acessório)…</option>
            {(produtos || []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.codigo} — {p.nome} ({p.unidade})
              </option>
            ))}
          </Select>
          <Input
            style={{ width: 110 }}
            placeholder="Quantidade"
            inputMode="decimal"
            value={novo.quantidade}
            onChange={(e) =>
              setNovo((p) => ({ ...p, quantidade: e.target.value }))
            }
            aria-label="Quantidade"
          />
          {produtoNovo && <InlineHint>{produtoNovo.unidade}</InlineHint>}
          <AsyncButton
            variant="secondary"
            onClick={adicionarLinha}
            disabled={!novoValido}
            successMessage="Ferragem adicionada."
          >
            <FiPlus size={12} /> Adicionar
          </AsyncButton>
          <SecondaryButton type="button" onClick={() => setNovo(null)}>
            <FiX size={12} /> Cancelar
          </SecondaryButton>
        </div>
      )}

      {temFita && <ComposicaoInfoBanner>{AVISO_FITA}</ComposicaoInfoBanner>}
    </section>
  );
}
